import { useBulkPhotoUploader } from '@/lib/photoLib';
import { useDraggable } from '@dnd-kit/core';
import { css } from '@emotion/react';
import { useScanEditingContextSlice, useScanIsEditableCheck } from '@kaiizen/kai-pro-api-react-state';
import { ScanGroupOptimized, ScanPhoto } from '@kaiizen/kai-scan-template-system';
import React, { useMemo } from 'react';
import { UploadIndicatorIcon } from '../../Icons';
import { useNavigateToPhoto, usePhotoUriForLayoutSize, useToast } from '../../State';
import { COLORS2, TYPOGRAPHY2 } from '../../Styles';
import { PhotoDropZone } from '../../Widgets/PhotoDropZone';
import { AddPhotoIcon } from '../../Widgets/StyledIcons';

const PHOTO_SIZE = 120;

const PHOTOS_TAB_STYLE = css`
  label: photosTab;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${COLORS2.alwaysDark.background.light};
`;

const PHOTOS_GRID_STYLE = css`
  label: photosGrid;
  display: grid;
  grid-template-columns: ${PHOTO_SIZE}px;
  grid-auto-rows: ${PHOTO_SIZE}px;
  flex: 1;
  overflow-y: auto;
`;

const PHOTO_ITEM_STYLE = css`
  label: photoItem;
  width: 100%;
  height: 0;
  padding-bottom: 100%;
  position: relative;
  overflow: hidden;
  touch-action: manipulation;
  cursor: grab;
  border: 0.5px solid black;

  &:active {
    cursor: grabbing;
  }
`;

const PHOTO_GRID_IMAGE_STYLE = css`
  label: photoGridImage;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PHOTO_UPLOAD_INDICATOR_STYLE = css`
  label: photoUploadIndicator;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  pointer-events: none;
`;

const ADD_PHOTO_BUTTON_STYLE = css`
  label: addPhotoButton;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${PHOTO_SIZE}px;
  height: ${PHOTO_SIZE}px;
  background: ${COLORS2.alwaysDark.background.light};
  cursor: pointer;
  transition: background-color 0.2s ease;
  gap: 4px;

  &:hover {
    background-color: ${COLORS2.alwaysDark.yellow};
  }
`;

const ADD_PHOTO_TEXT_STYLE = css`
  label: addPhotoText;
  ${TYPOGRAPHY2.xSmall}
  color: ${COLORS2.alwaysDark.text.darkGray};
  text-align: center;
  line-height: 1;
`;

const PhotoColumnThumbnail = ({ scanId, photo, index, onClick }: { scanId: string; photo: ScanPhoto; index: number; onClick: () => void }) => {
  const { photoId, photoSource } = photo;
  // A square tile: a 4:3 photo's long side spans PHOTO_SIZE × 4/3 once cropped to cover it.
  const uri = usePhotoUriForLayoutSize(photoId, (PHOTO_SIZE * 4) / 3);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `photo-${photoId}`,
    data: {
      // Carry the whole ScanPhoto record so the drop target keeps addedAt,
      // addedByUserId, tags, and photoSource — not just the photoId.
      photo,
      type: 'photo',
      uri,
    },
  });

  return (
    <div
      ref={setNodeRef}
      css={PHOTO_ITEM_STYLE}
      {...attributes}
      {...listeners}
      onClick={onClick}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {uri && <img src={uri} alt={`Photo ${index + 1}`} css={PHOTO_GRID_IMAGE_STYLE} draggable={false} />}
      {photoSource === 'upload' && (
        <div css={PHOTO_UPLOAD_INDICATOR_STYLE}>
          <UploadIndicatorIcon size={24} />
        </div>
      )}
    </div>
  );
};

export const PhotoColumn = ({
  group,
}: {
  group: ScanGroupOptimized;
}) => {
  const navigateToPhoto = useNavigateToPhoto();
  const { scan, scanRoot, scanDataMutators, scanIsEditable } = useScanEditingContextSlice(['scan', 'scanRoot', 'scanDataMutators', 'scanIsEditable']);
  const { showToast } = useToast();
  const checkEditableWithToast = useScanIsEditableCheck(showToast);

  const allPhotos = useMemo(() => {
    const photos = [...((group ?? scanRoot).optimized.allPhotos || [])];
    return photos.reverse();
  }, [group, scanRoot]);

  const bulkPhotoUploader = useBulkPhotoUploader();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!group) return;
    await bulkPhotoUploader(event.target.files, group, { groupId: group.id });
  };

  const handleAddPhotoClick = (event: React.MouseEvent<HTMLLabelElement>) => {
    if (!checkEditableWithToast()) {
      event.preventDefault();
    }
  };

  if (!scan) return null;
  return (
    <PhotoDropZone scanPart={group} scanPartContext={{ groupId: group?.id }}>
      <div css={PHOTOS_TAB_STYLE}>
        {scanIsEditable && (
          <label
            css={[ADD_PHOTO_BUTTON_STYLE]}
            title={group ? 'Add photo' : 'Select a group to add photos'}
            onClick={handleAddPhotoClick}
          >
            <input type='file' accept='image/*' multiple onChange={handleFileSelect} style={{ display: 'none' }} />
            <AddPhotoIcon color={COLORS2.alwaysDark.text.darkGray} width={16} height={16} />
            <div css={ADD_PHOTO_TEXT_STYLE}>Add Photo</div>
          </label>
        )}
        <div css={PHOTOS_GRID_STYLE}>
          {allPhotos.map((photo, index) => (
            <PhotoColumnThumbnail
              key={photo.photoId}
              scanId={scan.id}
              photo={photo}
              index={index}
              onClick={() => navigateToPhoto({ groupId: group?.id, photoId: photo.photoId })}
            />
          ))}
        </div>
      </div>
    </PhotoDropZone>
  );
};
